import { useMemo } from 'react';
import { Platform } from 'react-native';
import type {
  PaidPlanTier,
  PlanSet,
  PlanTier,
  RevenueCatStore,
  SubscriptionCatalog,
  SubscriptionSnapshot,
} from '@tabbio/types';
import { useTranslation } from 'react-i18next';
import { useSubscriptionState } from '../hooks/useSubscriptionState';
import { useWorkspace } from '../providers/useWorkspace';

type SubscriptionDisplayPlatform = 'native' | 'web';
type ResolvedSubscriptionPlatform = 'ios' | 'android' | 'web';
type ManageActionKind = 'manage_subscription' | 'manage_billing' | 'fallback' | 'none';
type SecondaryActionKind = 'restore' | 'sync' | 'none';
type SubscriptionTranslationFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;

type SubscriptionDisplayLabels = {
  freePlan: string;
  planSetLabels: Record<PlanSet, string>;
  tierLabels: Record<PlanTier, string>;
  upgradeLabels: Record<PlanSet, Record<PaidPlanTier, string>>;
  upgrade: string;
  subscription: string;
  manageSubscription: string;
  manageBilling: string;
  restorePurchases: string;
  syncSubscription: string;
  refreshSubscription: string;
  checkingSubscription: string;
  appStore: string;
  googlePlay: string;
  billingPortal: string;
  unknownStore: string;
  managedInAppStore: string;
  managedInGooglePlay: string;
  managedInBillingPortal: string;
  managedInUnknownStore: string;
  subscriptionActive: string;
  subscriptionTrial: string;
  subscriptionEnding: string;
  subscriptionExpired: string;
  subscriptionBillingIssue: string;
  checkingSubscriptionDescription: string;
  subscriptionRenewsOn: (date: string) => string;
  subscriptionTrialEndsOn: (date: string) => string;
  subscriptionAccessUntil: (date: string) => string;
  subscriptionExpiredOn: (date: string) => string;
  subscriptionBillingIssueDescription: string;
};

export type SubscriptionDisplayState = {
  planSet: PlanSet;
  tier: PlanTier;
  isFree: boolean;
  isPaid: boolean;
  isUnknownState: boolean;
  isLoadingSubscriptionState: boolean;
  tierLabel: string;
  fullPlanLabel: string;
  compactLabel: string;
  subscriptionRowLabel: string;
  billingActionLabel: string;
  showUpgradeAction: boolean;
  showManageAction: boolean;
  showRestoreAction: boolean;
  showSyncAction: boolean;
  canRestoreOnCurrentDevice: boolean;
  storeOriginLabel: string | null;
  billingStatusLabel: string | null;
  billingStatusDescription: string | null;
  managementDescription: string | null;
  managementFallbackUrl: string | null;
  manageActionKind: ManageActionKind;
  secondaryActionKind: SecondaryActionKind;
  upgradeTarget: PaidPlanTier | null;
  upgradeMode: PlanSet;
  upgradeLabel: string | null;
};

function resolveCurrentPlatform(
  platform: SubscriptionDisplayPlatform,
): ResolvedSubscriptionPlatform {
  if (platform === 'web') {
    return 'web';
  }

  if (Platform.OS === 'android') {
    return 'android';
  }

  return 'ios';
}

function formatDisplayDate(value: string | null, locale?: string) {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Intl.DateTimeFormat(locale || undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function isExpiredStatus(
  status: string | null | undefined,
  legacyPlan: string | null | undefined,
) {
  if (!legacyPlan || legacyPlan === 'free') {
    return false;
  }

  return status === 'expired' || status === 'inactive';
}

function isBillingIssueStatus(status?: string | null) {
  if (!status) {
    return false;
  }

  return /(billing|grace|past_due|unpaid)/i.test(status);
}

function isBillingPortalStore(store?: RevenueCatStore | null) {
  return store === 'rc_billing' || store === 'stripe' || store === 'test_store';
}

function getStoreOriginLabel(
  store: RevenueCatStore | null | undefined,
  labels: SubscriptionDisplayLabels,
) {
  switch (store) {
    case 'app_store':
      return labels.appStore;
    case 'play_store':
      return labels.googlePlay;
    case 'rc_billing':
    case 'stripe':
    case 'test_store':
      return labels.billingPortal;
    case 'unknown':
      return labels.unknownStore;
    default:
      return null;
  }
}

function getManagementFallbackUrl(store: RevenueCatStore | null | undefined) {
  switch (store) {
    case 'app_store':
      return 'https://account.apple.com';
    case 'play_store':
      return 'https://play.google.com/store/account/subscriptions';
    default:
      return null;
  }
}

function getManagementDescription(
  store: RevenueCatStore | null | undefined,
  labels: SubscriptionDisplayLabels,
) {
  switch (store) {
    case 'app_store':
      return labels.managedInAppStore;
    case 'play_store':
      return labels.managedInGooglePlay;
    case 'rc_billing':
    case 'stripe':
    case 'test_store':
      return labels.managedInBillingPortal;
    case 'unknown':
      return labels.managedInUnknownStore;
    default:
      return null;
  }
}

function getTierLabel(tier: PlanTier, labels: SubscriptionDisplayLabels) {
  return labels.tierLabels[tier];
}

function getUpgradeLabel(
  catalog: SubscriptionCatalog,
  planSet: PlanSet,
  tier: PaidPlanTier,
  labels: SubscriptionDisplayLabels,
) {
  return (
    labels.upgradeLabels[planSet]?.[tier] ??
    catalog.planSets[planSet].tiers[tier].marketing.cta
  );
}

export function buildSubscriptionDisplayLabels(
  t: SubscriptionTranslationFn,
): SubscriptionDisplayLabels {
  return {
    freePlan: t('settings.freePlan'),
    planSetLabels: {
      individual: t('settings.subscriptionPlans.planSets.individual'),
      employer: t('settings.subscriptionPlans.planSets.employer'),
    },
    tierLabels: {
      free: t('settings.subscriptionPlans.tiers.free'),
      plus: t('settings.subscriptionPlans.tiers.plus'),
      pro: t('settings.subscriptionPlans.tiers.pro'),
    },
    upgradeLabels: {
      individual: {
        plus: t('settings.subscriptionPlans.upgradeCtas.individual.plus'),
        pro: t('settings.subscriptionPlans.upgradeCtas.individual.pro'),
      },
      employer: {
        plus: t('settings.subscriptionPlans.upgradeCtas.employer.plus'),
        pro: t('settings.subscriptionPlans.upgradeCtas.employer.pro'),
      },
    },
    upgrade: t('upgrade.screenTitle'),
    subscription: t('settings.subscription'),
    manageSubscription: t('settings.manageSubscription'),
    manageBilling: t('settings.manageBilling'),
    restorePurchases: t('settings.restorePurchases'),
    syncSubscription: t('settings.syncSubscription'),
    refreshSubscription: t('settings.refreshSubscription'),
    checkingSubscription: t('settings.checkingSubscription'),
    appStore: t('settings.storeAppStore'),
    googlePlay: t('settings.storeGooglePlay'),
    billingPortal: t('settings.storeBillingPortal'),
    unknownStore: t('settings.storeUnknown'),
    managedInAppStore: t('settings.managedInAppStore'),
    managedInGooglePlay: t('settings.managedInGooglePlay'),
    managedInBillingPortal: t('settings.managedInBillingPortal'),
    managedInUnknownStore: t('settings.managedInUnknownStore'),
    subscriptionActive: t('settings.subscriptionActive'),
    subscriptionTrial: t('settings.subscriptionTrial'),
    subscriptionEnding: t('settings.subscriptionEnding'),
    subscriptionExpired: t('settings.subscriptionExpired'),
    subscriptionBillingIssue: t('settings.subscriptionBillingIssue'),
    checkingSubscriptionDescription: t('settings.subscriptionSyncingDescription'),
    subscriptionRenewsOn: (date) => t('settings.subscriptionRenewsOn', { date }),
    subscriptionTrialEndsOn: (date) => t('settings.subscriptionTrialEndsOn', { date }),
    subscriptionAccessUntil: (date) => t('settings.subscriptionAccessUntil', { date }),
    subscriptionExpiredOn: (date) => t('settings.subscriptionExpiredOn', { date }),
    subscriptionBillingIssueDescription: t('settings.subscriptionBillingIssueDescription'),
  };
}

export function selectSubscriptionDisplayState(input: {
  catalog: SubscriptionCatalog;
  snapshot: SubscriptionSnapshot | null;
  isLoading: boolean;
  platform: SubscriptionDisplayPlatform;
  workspaceMode: 'seeker' | 'employer';
  labels: SubscriptionDisplayLabels;
  locale?: string;
}): SubscriptionDisplayState {
  const {
    catalog,
    snapshot,
    labels,
    platform,
    workspaceMode,
    isLoading,
    locale,
  } = input;
  const currentPlatform = resolveCurrentPlatform(platform);
  const planSet =
    snapshot?.planSet ?? (workspaceMode === 'employer' ? 'employer' : 'individual');
  const tier = snapshot?.tier ?? 'free';
  const isLoadingSubscriptionState = isLoading && !snapshot;
  const isUnknownState = !snapshot;
  const isFree = !isLoadingSubscriptionState && tier === 'free';
  const isPaid = tier !== 'free';
  const paidTierLabel = isPaid ? getTierLabel(tier, labels) : null;
  const hasManagementUrl = Boolean(snapshot?.managementUrl);
  const managementFallbackUrl = getManagementFallbackUrl(snapshot?.store);
  const hasManagementFallback = Boolean(managementFallbackUrl);
  const managementDescription = getManagementDescription(snapshot?.store, labels);
  const hasManageDestination = hasManagementUrl || hasManagementFallback;
  const canRestoreOnCurrentDevice = Boolean(
    isPaid &&
      ((snapshot?.store === 'app_store' && currentPlatform === 'ios') ||
        (snapshot?.store === 'play_store' && currentPlatform === 'android')),
  );
  const isExpired = isExpiredStatus(snapshot?.status, snapshot?.legacyPlan);
  const showManageAction = Boolean((isPaid || isExpired) && hasManageDestination);
  const showRestoreAction = canRestoreOnCurrentDevice;
  const showSyncAction = !isLoadingSubscriptionState && !showRestoreAction;
  const upgradeTarget: PaidPlanTier | null =
    tier === 'free' ? 'plus' : tier === 'plus' ? 'pro' : null;
  const fullPlanLabel = isLoadingSubscriptionState
    ? labels.checkingSubscription
    : paidTierLabel && planSet === 'employer'
      ? `${labels.planSetLabels.employer} ${paidTierLabel}`
      : paidTierLabel ?? labels.freePlan;
  const tierLabel = isLoadingSubscriptionState
    ? labels.checkingSubscription
    : paidTierLabel ?? labels.freePlan;
  const compactLabel = isLoadingSubscriptionState
    ? labels.checkingSubscription
    : isFree
      // Count hidden for now: `${dailyRemaining}/${dailyLimit} · ${labels.upgrade}`
      ? labels.upgrade
      : paidTierLabel ?? labels.upgrade;
  const subscriptionRowLabel = showManageAction
    ? isBillingPortalStore(snapshot?.store)
      ? labels.manageBilling
      : labels.manageSubscription
    : labels.subscription;
  const billingActionLabel = showRestoreAction
    ? labels.restorePurchases
    : isBillingPortalStore(snapshot?.store)
      ? labels.refreshSubscription
      : labels.syncSubscription;
  const upgradeLabel =
    !isLoadingSubscriptionState && upgradeTarget
      ? getUpgradeLabel(catalog, planSet, upgradeTarget, labels)
      : null;

  const renewsAt = formatDisplayDate(snapshot?.renewsAt ?? null, locale);
  const trialEndsAt = formatDisplayDate(snapshot?.trialEndsAt ?? null, locale);
  const billingStatusLabel = isLoadingSubscriptionState
    ? labels.checkingSubscription
    : isExpired
      ? labels.subscriptionExpired
      : snapshot?.status === 'trialing'
        ? labels.subscriptionTrial
        : isPaid && snapshot?.willRenew === false
          ? labels.subscriptionEnding
          : isBillingIssueStatus(snapshot?.status)
            ? labels.subscriptionBillingIssue
            : isPaid
              ? labels.subscriptionActive
              : null;
  const billingStatusDescription = isLoadingSubscriptionState
    ? labels.checkingSubscriptionDescription
    : isExpired
      ? renewsAt
        ? labels.subscriptionExpiredOn(renewsAt)
        : null
      : snapshot?.status === 'trialing'
        ? trialEndsAt
          ? labels.subscriptionTrialEndsOn(trialEndsAt)
          : null
        : isPaid && snapshot?.willRenew === false
          ? renewsAt
            ? labels.subscriptionAccessUntil(renewsAt)
            : null
          : isBillingIssueStatus(snapshot?.status)
            ? renewsAt
              ? labels.subscriptionAccessUntil(renewsAt)
              : labels.subscriptionBillingIssueDescription
            : isPaid && renewsAt
              ? labels.subscriptionRenewsOn(renewsAt)
              : null;
  const manageActionKind: ManageActionKind = !showManageAction
    ? 'none'
    : isBillingPortalStore(snapshot?.store)
      ? 'manage_billing'
      : hasManagementUrl
        ? 'manage_subscription'
        : 'fallback';
  const secondaryActionKind: SecondaryActionKind = showRestoreAction
    ? 'restore'
    : showSyncAction
      ? 'sync'
      : 'none';

  return {
    planSet,
    tier,
    isFree,
    isPaid,
    isUnknownState,
    isLoadingSubscriptionState,
    tierLabel,
    fullPlanLabel,
    compactLabel,
    subscriptionRowLabel,
    billingActionLabel,
    showUpgradeAction: Boolean(!isLoadingSubscriptionState && upgradeTarget),
    showManageAction,
    showRestoreAction,
    showSyncAction,
    canRestoreOnCurrentDevice,
    storeOriginLabel: getStoreOriginLabel(snapshot?.store, labels),
    billingStatusLabel,
    billingStatusDescription,
    managementDescription,
    managementFallbackUrl,
    manageActionKind,
    secondaryActionKind,
    upgradeTarget,
    upgradeMode: planSet,
    upgradeLabel,
  };
}

export function useSubscriptionDisplay(platform: SubscriptionDisplayPlatform) {
  const { t, i18n } = useTranslation('common');
  const { catalog, snapshot, isLoading } = useSubscriptionState();
  const { mode } = useWorkspace();

  return useMemo(
    () =>
      selectSubscriptionDisplayState({
        catalog,
        snapshot,
        isLoading,
        platform,
        workspaceMode: mode,
        locale: i18n.language,
        labels: buildSubscriptionDisplayLabels((key, options) => t(key, options)),
      }),
    [catalog, i18n.language, isLoading, mode, platform, snapshot, t],
  );
}
